import useGuardedRealtimeList, { readListRows } from '../../realtime/useGuardedRealtimeList';
import { readScheduleRows } from '../../realtime/useScheduleRealtime';
import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, Col, Form, Row, Spinner, Table, Modal } from "react-bootstrap";
import moment from "moment";
import { fetchData } from "../../redux/slices/apiCallSlice";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { getProjectId, getProjectStatus, idsMatch } from "../../utils/dataNormalization";
import { formatDateUnified } from "../../utils/dateUtils";
import PdfViewerModal from "../common/PdfViewerModal";
import axiosInstance from "../../axios/axiosConfig";
import { fetchBackgroundData } from '../../redux/api/portalApi';

const NP_SCHEDULE_ATTACHMENT_FIELD = "SharePointFileIds";
const NP_SCHEDULE_ATTACHMENT_ID_DELIMITER = "|";
const PROJECT_NAME_COLUMN_STYLE = {
  minWidth: "280px",
  width: "280px",
  whiteSpace: "normal",
};
const STICKY_JOB_NUMBER_COL_WIDTH = 130;
const STICKY_PROJECT_NAME_COL_WIDTH = 280;
const SHIPPED_ENCLOSURES_TABLE_SCROLL_CONTAINER_STYLE = {
  position: "relative",
  maxHeight: "78vh",
  overflowX: "auto",
  overflowY: "auto",
};
const STICKY_COLUMN_LEFT = {
  jobNumber: 0,
  projectName: STICKY_JOB_NUMBER_COL_WIDTH,
};
const STICKY_HEADER_STYLE = {
  position: "sticky",
  top: 0,
  backgroundColor: "#f8f9fa",
  zIndex: 8,
};
const STICKY_MONTH_ROW_STYLE = {
  position: "sticky",
  top: "41px",
  backgroundColor: "#f8f9fa",
  color: "#212529",
  fontWeight: "bold",
  padding: "8px",
  zIndex: 7,
};
const getStickyHeaderColumnStyle = (columnKey, extraStyle = {}) => ({
  ...STICKY_HEADER_STYLE,
  left: STICKY_COLUMN_LEFT[columnKey] ?? 0,
  zIndex: 10,
  ...extraStyle,
});
const getStickyBodyColumnStyle = (columnKey, extraStyle = {}) => ({
  position: "sticky",
  left: STICKY_COLUMN_LEFT[columnKey] ?? 0,
  backgroundColor: "#fff",
  zIndex: 4,
  ...extraStyle,
});

const getNpScheduleProjectId = (record) => (
  record?.ProjectID ??
  record?.projectID ??
  record?.ProjectId ??
  record?.projectId ??
  null
);

const getNpScheduleAttachmentIdsValue = (record) => (
  record?.[NP_SCHEDULE_ATTACHMENT_FIELD] ??
  record?.sharePointFileIds ??
  null
);

const normalizeSearchValue = (value) => String(value ?? "").trim().toLowerCase();

const findNpScheduleMatch = (project, scheduleRows) => {
  const projectId = getProjectId(project);

  const projectIdMatch = scheduleRows.find((record) =>
    idsMatch(getNpScheduleProjectId(record), projectId)
  );
  if (projectIdMatch) {
    return projectIdMatch;
  }

  const jobNumber = normalizeSearchValue(project?.JobNumber);
  if (jobNumber) {
    const jobNumberMatch = scheduleRows.find(
      (record) => normalizeSearchValue(record?.JobNumber) === jobNumber
    );
    if (jobNumberMatch) {
      return jobNumberMatch;
    }
  }

  const projectName = normalizeSearchValue(project?.ProjectName);
  if (projectName) {
    return scheduleRows.find(
      (record) => normalizeSearchValue(record?.ProjectName) === projectName
    );
  }

  return null;
};

const parseSharePointFileIds = (value) => {
  const uniqueIds = new Set(
    String(value ?? "")
      .split(NP_SCHEDULE_ATTACHMENT_ID_DELIMITER)
      .map((entry) => entry.trim())
      .filter(Boolean)
  );

  return Array.from(uniqueIds);
};

const getGraphItemDisplayUrl = (item) => (
  item?.webUrl || item?.["@microsoft.graph.downloadUrl"] || ""
);

const isViewableAttachmentFile = (fileName) => {
  const extension = String(fileName ?? "").split(".").pop()?.toLowerCase();
  return extension === "pdf";
};

const formatAttachmentFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) {
    return "-";
  }

  const size = Number(bytes);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getInsulatedDisplay = (job) => {
  const acoustic = String(job?.AcousticInsulation ?? "").trim();
  const thermal = String(job?.ThermalInsulation ?? "").trim();

  if (acoustic && thermal) {
    return `${acoustic} / ${thermal}`;
  }

  return acoustic || thermal || "";
};

const getComparableFilterValue = (value) => String(value ?? "").trim();

const getFilterOptionLabel = (value) => {
  const displayValue = getComparableFilterValue(value);
  return displayValue || "Blank";
};

const getFilterValueForJob = (job, filterKey) => {
  if (filterKey === "Insulated") {
    return getComparableFilterValue(getInsulatedDisplay(job));
  }

  return getComparableFilterValue(job?.[filterKey]);
};

const SHIPPED_ENCLOSURE_FILTERS = [
  { key: "BaseWidth", label: "Width" },
  { key: "BaseLength", label: "Length" },
  { key: "Color", label: "Color" },
  { key: "SnowWindRating", label: "Wind Rated" },
  { key: "Insulated", label: "Insulated" },
];

const sortFilterValues = (values) => (
  Array.from(values).sort((left, right) => left.localeCompare(right, undefined, {
    numeric: true,
    sensitivity: "base",
  }))
);

const matchesSelectedFilters = (job, selectedFilters, excludedFilterKey = null) => (
  SHIPPED_ENCLOSURE_FILTERS.every((filter) => {
    if (filter.key === excludedFilterKey) {
      return true;
    }

    const selectedValues = selectedFilters[filter.key] || [];

    if (selectedValues.length === 0) {
      return true;
    }

    return selectedValues.includes(getFilterValueForJob(job, filter.key));
  })
);

const getShippedMonthKey = (job) => {
  const shipDate = moment(job?.ActualShipDate);
  return shipDate.isValid() ? shipDate.format("YYYY-MM") : "unknown";
};

const getEnclosureColor = (project, enclosure, station) => {
  const enclosureColor = String(enclosure?.EnclosurePaintColor ?? "").trim();
  const stationColor = String(station?.Color ?? "").trim();
  const projectColor = String(project?.Color ?? "").trim();

  if (enclosureColor === "Same as Station") {
    return stationColor || projectColor || "N/A";
  }

  return enclosureColor || stationColor || projectColor || "N/A";
};

const normalizeFullScheduleProjects = (response) => {
  const raw = response?.data || response || {};

  if (Array.isArray(raw)) {
    return raw.map((item) => item?.GeneralProjectInfo || item).filter(Boolean);
  }

  return Object.values(raw)
    .map((item) => item?.GeneralProjectInfo || item)
    .filter(Boolean);
};

function buildShippedEnclosures(generalProjectData, stationEnclosureData, generalStationData, npScheduleData) {
        const projects = normalizeFullScheduleProjects(generalProjectData);
        const enclosures = Array.isArray(stationEnclosureData) ? stationEnclosureData : [];
        const stations = Array.isArray(generalStationData) ? generalStationData : [];
        const npScheduleRows = Array.isArray(npScheduleData) ? npScheduleData : [];

        const shippedEnclosures = projects
          .map((project) => {
            const projectId = getProjectId(project);
            const enclosure = enclosures.find((item) => idsMatch(getProjectId(item), projectId));
            const station = stations.find((item) => idsMatch(getProjectId(item), projectId));
            const npScheduleRecord = findNpScheduleMatch(project, npScheduleRows);

            return {
              ...project,
              ...(enclosure || {}),
              ...(station || {}),
              ProjectID: projectId,
              ProjectRecordID: project?.ID ?? projectId,
              Color: getEnclosureColor(project, enclosure, station),
              [NP_SCHEDULE_ATTACHMENT_FIELD]: getNpScheduleAttachmentIdsValue(npScheduleRecord),
            };
          })
          .filter((job) =>
            getProjectStatus(job) === "Shipped" &&
            job.ActualShipDate &&
            job.HideFromList !== true &&
            job.HideFromList !== "true" &&
            job.ScheduleOnly !== "Yes" &&
            job.HasEnclosure === true &&
            job.EnclosureType === "CSM"
          )
          .sort((left, right) => {
            const leftDate = new Date(left.ActualShipDate);
            const rightDate = new Date(right.ActualShipDate);

            if (Number.isNaN(leftDate.getTime())) return 1;
            if (Number.isNaN(rightDate.getTime())) return -1;

            return rightDate.getTime() - leftDate.getTime();
          });

  return shippedEnclosures;
}

function ShippedEnclosuresTable() {
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attachmentError, setAttachmentError] = useState("");
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [loadingAttachmentViewer, setLoadingAttachmentViewer] = useState(false);
  const [attachmentViewerUrl, setAttachmentViewerUrl] = useState("");
  const [attachmentViewerExternalUrl, setAttachmentViewerExternalUrl] = useState("");
  const [attachmentViewerTitle, setAttachmentViewerTitle] = useState("");
  const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
  const [attachmentPickerItems, setAttachmentPickerItems] = useState([]);
  const [attachmentPickerJobLabel, setAttachmentPickerJobLabel] = useState("");
  const [selectedFilters, setSelectedFilters] = useState(() => (
    SHIPPED_ENCLOSURE_FILTERS.reduce((accumulator, filter) => {
      accumulator[filter.key] = [];
      return accumulator;
    }, {})
  ));

  const npSharePointSiteId = process.env.REACT_APP_SHAREPOINT_NP_SITE_ID;
  const npSharePointDriveId = process.env.REACT_APP_SHAREPOINT_NP_DRIVE_ID;
  const hasNpSharePointConfig = Boolean(npSharePointSiteId && npSharePointDriveId);

  const acquireGraphAccessToken = useCallback(async () => {
    const account = accounts?.[0];

    if (!instance || !account) {
      throw new Error("Unable to access SharePoint because no signed-in account is available.");
    }

    const tokenResponse = await instance.acquireTokenSilent({
      scopes: ["https://graph.microsoft.com/.default"],
      account,
    });

    return tokenResponse.accessToken;
  }, [accounts, instance]);

  const fetchGraphItemById = useCallback(async (itemId, accessToken) => {
    const response = await axiosInstance.get(
      `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${itemId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  }, [npSharePointDriveId, npSharePointSiteId]);

  useEffect(() => {
    let isMounted = true;

    const loadRows = async () => {
      if (!apiUrls.fullSchedule || !apiUrls.stationEnclosure || !apiUrls.generalStation || !apiUrls.npSchedule) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const [generalProjectData, stationEnclosureData, generalStationData, npScheduleData] = await Promise.all([
          dispatch(fetchData({ url: `${apiUrls.fullSchedule}?jobStatus=Shipped` })).unwrap(),
          dispatch(fetchData({ url: apiUrls.stationEnclosure })).unwrap(),
          dispatch(fetchData({ url: apiUrls.generalStation })).unwrap(),
          dispatch(fetchData({ url: apiUrls.npSchedule })).unwrap(),
        ]);

        const shippedEnclosures = buildShippedEnclosures(generalProjectData, stationEnclosureData, generalStationData, npScheduleData);

        if (isMounted) {
          setRows(shippedEnclosures);
        }
      } catch (fetchError) {
        console.error("Failed to load shipped enclosures:", fetchError);
        if (isMounted) {
          setError(fetchError?.message || "Failed to load shipped enclosures.");
          setRows([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadRows();

    return () => {
      isMounted = false;
    };
  }, [apiUrls.fullSchedule, apiUrls.generalStation, apiUrls.npSchedule, apiUrls.stationEnclosure, dispatch]);

  useGuardedRealtimeList({ apiKey: 'fullSchedule', scopeKey: apiUrls, blocked: loading,
    additionalResources: ['portal:general-project-info', 'portal:station-enclosure', 'portal:general-station-info', 'portal:np-schedule'],
    load: async ({ signal }) => {
      const [schedule, enclosures, stations, np] = await Promise.all([
        dispatch(fetchBackgroundData({ url: apiUrls.fullSchedule, signal, params: { jobStatus: 'Shipped' } })),
        dispatch(fetchBackgroundData({ url: apiUrls.stationEnclosure, signal })),
        dispatch(fetchBackgroundData({ url: apiUrls.generalStation, signal })),
        dispatch(fetchBackgroundData({ url: apiUrls.npSchedule, signal })),
      ]);
      readScheduleRows(schedule);
      return buildShippedEnclosures(schedule, readListRows(enclosures), readListRows(stations), readListRows(np));
    }, apply: setRows,
  });

  const filterOptionsByKey = useMemo(() => (
    SHIPPED_ENCLOSURE_FILTERS.reduce((accumulator, filter) => {
      const uniqueValues = new Set(
        rows.map((job) => getFilterValueForJob(job, filter.key))
      );

      accumulator[filter.key] = sortFilterValues(uniqueValues);

      return accumulator;
    }, {})
  ), [rows]);

  const availableFilterValuesByKey = useMemo(() => (
    SHIPPED_ENCLOSURE_FILTERS.reduce((accumulator, filter) => {
      const availableValues = new Set(
        rows
          .filter((job) => matchesSelectedFilters(job, selectedFilters, filter.key))
          .map((job) => getFilterValueForJob(job, filter.key))
      );

      accumulator[filter.key] = availableValues;

      return accumulator;
    }, {})
  ), [rows, selectedFilters]);

  const filteredRows = useMemo(() => (
    rows.filter((job) => matchesSelectedFilters(job, selectedFilters))
  ), [rows, selectedFilters]);

  const hasActiveFilters = useMemo(() => (
    SHIPPED_ENCLOSURE_FILTERS.some((filter) => (selectedFilters[filter.key] || []).length > 0)
  ), [selectedFilters]);

  const handleFilterChange = useCallback((filterKey, event) => {
    const values = Array.from(event.target.selectedOptions).map((option) => option.value);

    setSelectedFilters((previousFilters) => ({
      ...previousFilters,
      [filterKey]: values,
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedFilters(
      SHIPPED_ENCLOSURE_FILTERS.reduce((accumulator, filter) => {
        accumulator[filter.key] = [];
        return accumulator;
      }, {})
    );
  }, []);

  const groupedRows = useMemo(() => {
    const groups = filteredRows.reduce((accumulator, job) => {
      const monthKey = getShippedMonthKey(job);
      const shipDate = moment(job.ActualShipDate);
      const label = shipDate.isValid() ? shipDate.format("MMMM YYYY") : "Unknown Ship Date";

      if (!accumulator[monthKey]) {
        accumulator[monthKey] = { label, rows: [] };
      }

      accumulator[monthKey].rows.push(job);
      return accumulator;
    }, {});

    const sortedMonthKeys = Object.keys(groups).sort((left, right) => {
      if (left === "unknown") return 1;
      if (right === "unknown") return -1;
      return moment(right, "YYYY-MM").diff(moment(left, "YYYY-MM"));
    });

    return { groups, sortedMonthKeys };
  }, [filteredRows]);

  const handleCloseAttachmentViewer = useCallback(() => {
    setShowAttachmentViewer(false);
    setAttachmentViewerUrl((previousUrl) => {
      if (previousUrl && previousUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }

      return "";
    });
    setAttachmentViewerExternalUrl("");
    setAttachmentViewerTitle("");
    setLoadingAttachmentViewer(false);
  }, []);

  const handleCloseAttachmentPicker = useCallback(() => {
    setShowAttachmentPicker(false);
    setAttachmentPickerItems([]);
    setAttachmentPickerJobLabel("");
  }, []);

  const handleOpenAttachmentInViewer = useCallback(async (item, providedAccessToken = null) => {
    const targetUrl = getGraphItemDisplayUrl(item);

    if (!targetUrl) {
      setAttachmentError("Unable to open attachment because no SharePoint URL is available.");
      return;
    }

    setAttachmentError("");
    setAttachmentViewerTitle(item?.name || "Attachment");
    setAttachmentViewerExternalUrl(targetUrl);
    setAttachmentViewerUrl((previousUrl) => {
      if (previousUrl && previousUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }

      return "";
    });
    setLoadingAttachmentViewer(true);
    setShowAttachmentViewer(true);

    if (!isViewableAttachmentFile(item?.name)) {
      setLoadingAttachmentViewer(false);
      return;
    }

    try {
      const accessToken = providedAccessToken || await acquireGraphAccessToken();
      const graphContentUrl = `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${item?.id}/content`;

      const response = await axiosInstance.get(graphContentUrl, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const objectUrl = URL.createObjectURL(response.data);
      setAttachmentViewerUrl(objectUrl);
    } catch (viewerError) {
      console.error("Failed to load attachment preview:", viewerError);
      setAttachmentViewerUrl("");
      setAttachmentError(viewerError?.message || "Failed to load attachment preview. Use Open in New Tab as fallback.");
    } finally {
      setLoadingAttachmentViewer(false);
    }
  }, [acquireGraphAccessToken, npSharePointDriveId, npSharePointSiteId]);

  const handleViewRowAttachments = useCallback(async (event, job) => {
    event.stopPropagation();

    const fileIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(job));
    if (fileIds.length === 0) {
      return;
    }

    if (!hasNpSharePointConfig) {
      setAttachmentError("NP SharePoint attachment configuration is missing.");
      return;
    }

    try {
      const accessToken = await acquireGraphAccessToken();
      const fetchedItems = await Promise.all(fileIds.map(async (fileId) => {
        try {
          return await fetchGraphItemById(fileId, accessToken);
        } catch (_) {
          return null;
        }
      }));

      const validItems = fetchedItems.filter((item) => item && getGraphItemDisplayUrl(item));
      if (validItems.length === 0) {
        setAttachmentError("Unable to load attachment metadata for this row.");
        return;
      }

      if (validItems.length > 1) {
        setAttachmentPickerItems(validItems);
        setAttachmentPickerJobLabel(
          String(job?.JobNumber ?? "").trim() || String(job?.ProjectName ?? "").trim() || "Selected Row"
        );
        setShowAttachmentPicker(true);
        return;
      }

      const preferredItem = validItems.find((item) => isViewableAttachmentFile(item?.name)) || validItems[0];
      await handleOpenAttachmentInViewer(preferredItem, accessToken);
    } catch (rowAttachmentError) {
      console.error("Failed to load row attachments:", rowAttachmentError);
      setAttachmentError(rowAttachmentError?.message || "Failed to load row attachments.");
    }
  }, [acquireGraphAccessToken, fetchGraphItemById, handleOpenAttachmentInViewer, hasNpSharePointConfig]);

  const handleSelectAttachmentFromPicker = useCallback(async (item) => {
    handleCloseAttachmentPicker();
    await handleOpenAttachmentInViewer(item);
  }, [handleCloseAttachmentPicker, handleOpenAttachmentInViewer]);

  useEffect(() => {
    return () => {
      if (attachmentViewerUrl && attachmentViewerUrl.startsWith("blob:")) {
        URL.revokeObjectURL(attachmentViewerUrl);
      }
    };
  }, [attachmentViewerUrl]);

  if (loading) {
    return (
      <div className="m-4 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-4">
        {error}
      </Alert>
    );
  }

  return (
    <div className="m-3">
      {attachmentError && (
        <Alert variant="warning" className="mb-3" dismissible onClose={() => setAttachmentError("")}>
          {attachmentError}
        </Alert>
      )}

      {rows.length === 0 ? (
        <Alert variant="info">No shipped CSM enclosures found.</Alert>
      ) : (
        <>
        <div className="mb-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <div className="fw-semibold">
              Showing {filteredRows.length} of {rows.length} shipped enclosures
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear Filters
            </Button>
          </div>
          <Row className="g-2">
            {SHIPPED_ENCLOSURE_FILTERS.map((filter) => (
              <Col key={filter.key} md={4} lg>
                <Form.Group controlId={`shipped-enclosure-filter-${filter.key}`}>
                  <Form.Label className="small fw-semibold mb-1">{filter.label}</Form.Label>
                  <Form.Select
                    multiple
                    size="sm"
                    value={selectedFilters[filter.key] || []}
                    onChange={(event) => handleFilterChange(filter.key, event)}
                    style={{ minHeight: "7rem" }}
                  >
                    {(filterOptionsByKey[filter.key] || []).filter((value) => {
                      const selectedValues = selectedFilters[filter.key] || [];
                      const isAvailable = availableFilterValuesByKey[filter.key]?.has(value);
                      const isSelected = selectedValues.includes(value);

                      return isAvailable || isSelected;
                    }).map((value) => {
                      const selectedValues = selectedFilters[filter.key] || [];
                      const isAvailable = availableFilterValuesByKey[filter.key]?.has(value);
                      const isSelected = selectedValues.includes(value);

                      return (
                      <option
                        key={`${filter.key}-${value || "__blank__"}`}
                        value={value}
                      >
                        {getFilterOptionLabel(value)}{!isAvailable && isSelected ? " (no matches)" : ""}
                      </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>
              </Col>
            ))}
          </Row>
        </div>

        {filteredRows.length === 0 ? (
          <Alert variant="info">No shipped CSM enclosures match the selected filters.</Alert>
        ) : (
          <div
            className="table-responsive"
            style={SHIPPED_ENCLOSURES_TABLE_SCROLL_CONTAINER_STYLE}
          >
          <Table striped bordered hover>
          <thead>
            <tr>
              <th
                style={getStickyHeaderColumnStyle("jobNumber", {
                  width: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                  minWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                  maxWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                })}
              >
                Job Number
              </th>
              <th
                style={getStickyHeaderColumnStyle("projectName", {
                  ...PROJECT_NAME_COLUMN_STYLE,
                  width: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                  minWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                  maxWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                })}
              >
                Project Name
              </th>
              <th style={STICKY_HEADER_STYLE}>Model Number</th>
              <th style={STICKY_HEADER_STYLE}>Width</th>
              <th style={STICKY_HEADER_STYLE}>Length</th>
              <th style={STICKY_HEADER_STYLE}>Color</th>
              <th style={STICKY_HEADER_STYLE}>Wind Rated</th>
              <th style={STICKY_HEADER_STYLE}>Insulated</th>
              <th style={STICKY_HEADER_STYLE}>Actual Ship Date</th>
              <th style={STICKY_HEADER_STYLE}>Attachment</th>
            </tr>
          </thead>
          <tbody>
            {groupedRows.sortedMonthKeys.map((monthKey) => {
              const monthGroup = groupedRows.groups[monthKey];

              return (
                <React.Fragment key={monthKey}>
                  <tr>
                    <td
                      colSpan={10}
                      style={STICKY_MONTH_ROW_STYLE}
                    >
                      {monthGroup.label} - Shipped Enclosures: {monthGroup.rows.length}
                    </td>
                  </tr>
                  {monthGroup.rows.map((job) => {
                    const attachmentCount = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(job)).length;
                    const projectId = job.ProjectRecordID ?? job.ProjectID;

                    return (
                      <tr key={`${projectId}-${job.JobNumber}`}>
                        <td
                          className="align-middle"
                          style={getStickyBodyColumnStyle("jobNumber", {
                            width: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                            minWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                            maxWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                          })}
                        >
                          {projectId && canOpenPortalJob(job) ? (
                            <a href={`/${projectId}/generalInfo`} style={{ textDecoration: "none", color: "inherit" }}>
                              {job.JobNumber}
                            </a>
                          ) : (
                            job.JobNumber
                          )}
                        </td>
                        <td
                          className="align-middle"
                          style={getStickyBodyColumnStyle("projectName", {
                            ...PROJECT_NAME_COLUMN_STYLE,
                            width: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                            minWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                            maxWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                          })}
                        >
                          {job.ProjectName}
                        </td>
                        <td className="align-middle">{job.ModelNumber}</td>
                        <td className="align-middle">{job.BaseWidth}</td>
                        <td className="align-middle">{job.BaseLength}</td>
                        <td className="align-middle">{job.Color}</td>
                        <td className="align-middle">{job.SnowWindRating}</td>
                        <td className="align-middle">{getInsulatedDisplay(job)}</td>
                        <td className="align-middle">{formatDateUnified(job.ActualShipDate, "display")}</td>
                        <td className="text-center align-middle">
                          {attachmentCount > 0 ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={(event) => handleViewRowAttachments(event, job)}
                              title={`View attachment${attachmentCount === 1 ? "" : "s"} (${attachmentCount})`}
                              aria-label={`View attachment${attachmentCount === 1 ? "" : "s"} (${attachmentCount})`}
                            >
                              <i className="bi bi-paperclip" />
                            </Button>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
        </div>
        )}
        </>
      )}

      <Modal
        show={showAttachmentPicker}
        onHide={handleCloseAttachmentPicker}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Select Attachment{attachmentPickerJobLabel ? ` - ${attachmentPickerJobLabel}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Table bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ width: "120px" }}>Size</th>
                <th style={{ width: "90px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {attachmentPickerItems.map((item) => (
                <tr key={item?.id || item?.name}>
                  <td>{item?.name || "Unknown file"}</td>
                  <td>{formatAttachmentFileSize(item?.size)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleSelectAttachmentFromPicker(item)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAttachmentPicker}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <PdfViewerModal
        show={showAttachmentViewer}
        onHide={handleCloseAttachmentViewer}
        title={attachmentViewerTitle || "Attachment Viewer"}
        loading={loadingAttachmentViewer}
        viewerUrl={attachmentViewerUrl}
        externalUrl={attachmentViewerExternalUrl}
        dialogClassName="engineering-pdf-viewer-modal"
      />
    </div>
  );
}

export default ShippedEnclosuresTable;
